import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import {
  CampaignVariableKey,
  variableLabel,
  variableToken,
} from '../../../../utils/campaignTemplateVariables';
import Tooltip from '../../../../fragments/Tooltip';

interface Props {
  variableKey: CampaignVariableKey;
  value: string;
  anchorRect: DOMRect | null;
  mode?: 'insert' | 'edit';
  onSave: (value: string, saveAsUserDefault?: boolean) => void;
  onClose: () => void;
}

const VariableFallbackPopover: React.FC<Props> = ({
  variableKey,
  value,
  anchorRect,
  mode = 'edit',
  onSave,
  onClose,
}) => {
  const [draft, setDraft] = useState(value);
  const [saveDefault, setSaveDefault] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(value);
    setSaveDefault(true);
  }, [value, variableKey]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [variableKey]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  const panelH = 200;
  const gap = 8;
  const spaceBelow = window.innerHeight - anchorRect.bottom - gap;
  const spaceAbove = anchorRect.top - gap;
  const placeAbove = spaceBelow < panelH && spaceAbove > spaceBelow;
  const top = placeAbove ? anchorRect.top - gap : anchorRect.bottom + gap;
  const left = Math.max(12, Math.min(anchorRect.left, window.innerWidth - 340));

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSave(trimmed, saveDefault);
  };

  return createPortal(
    <Panel ref={panelRef} $place={placeAbove ? 'above' : 'below'} style={{ top, left }}>
      <VarPill>{variableLabel(variableKey)}</VarPill>
      <Row>
        <InputWrap>
          <Input
            ref={inputRef}
            value={draft}
            placeholder="Enter fallback text"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <InfoIcon type="button" tabIndex={-1} aria-label="Info">
            i
            <Tooltip title="Leads without this field will use this fallback text when the message is sent." />
          </InfoIcon>
        </InputWrap>
        <SaveBtn type="button" disabled={!draft.trim()} onClick={handleSubmit}>
          {mode === 'insert' ? 'Insert' : 'Save'}
        </SaveBtn>
      </Row>
      <Hint>
        Used when <code>{variableToken(variableKey)}</code> has no value for a lead.
      </Hint>
      <DefaultRow>
        <DefaultCheck
          type="checkbox"
          checked={saveDefault}
          onChange={e => setSaveDefault(e.target.checked)}
          id={`var-default-${variableKey}`}
        />
        <label htmlFor={`var-default-${variableKey}`}>Also use as my default for new campaigns</label>
      </DefaultRow>
    </Panel>,
    document.body,
  );
};

export default VariableFallbackPopover;

const Panel = styled.div<{ $place: 'above' | 'below' }>`
  position: fixed;
  z-index: 10050;
  width: 320px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e8ecf4;
  transform: ${({ $place }) => ($place === 'above' ? 'translateY(-100%)' : 'none')};
  box-shadow: ${({ $place }) => (
    $place === 'above'
      ? '0 -12px 40px rgba(15, 23, 42, 0.14), 0 -2px 8px rgba(15, 23, 42, 0.06)'
      : '0 12px 40px rgba(15, 23, 42, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06)'
  )};
`;

const VarPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${styles.colors.primary100};
  color: ${styles.colors.primary600};
  font-family: NunitoSans, sans-serif;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputWrap = styled.div`
  position: relative;
  flex: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 32px 0 10px;
  border: 1px solid #dfe5ef;
  border-radius: 8px;
  font-family: NunitoSans, sans-serif;
  font-size: 13px;
  color: ${styles.colors.black600};
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${styles.colors.primary600};
    box-shadow: 0 0 0 3px rgba(88, 106, 232, 0.12);
  }
`;

const InfoIcon = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #eef1f7;
  color: #8b95a8;
  font-size: 11px;
  font-weight: 700;
  font-style: italic;
  cursor: help;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover .LDtooltip { display: block; }
  .LDtooltip {
    top: -30px;
    left: 50%;
    white-space: normal;
    width: max-content;
    max-width: 220px;
    text-align: center;
    line-height: 1.35;
    padding: 6px 8px;
  }
`;

const SaveBtn = styled.button`
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: #0f172a;
  color: #fff;
  font-family: NunitoSans, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Hint = styled.p`
  margin: 8px 0 0;
  font-family: NunitoSans, sans-serif;
  font-size: 11px;
  color: #8b95a8;
  line-height: 1.4;
  code {
    font-size: 11px;
    color: ${styles.colors.primary600};
    background: #f4f6fb;
    padding: 1px 4px;
    border-radius: 4px;
  }
`;

const DefaultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-family: NunitoSans, sans-serif;
  font-size: 11px;
  color: #64748b;
  label { cursor: pointer; user-select: none; }
`;

const DefaultCheck = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;
